import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import { Checkbox } from 'semantic-ui-react';
import _ from 'lodash';
import { caseInsensitiveFilter } from 'Common/GridHelper';

import MPWWebApi from 'API/MPWApi';

/**
 *
 * @param {Object} selected
 */
function countSelected(selectedMap) {
  return Object.values(selectedMap).filter((isChecked) => isChecked).length;
}

class SelectSitesTable extends Component {
  static propTypes = {
    onChange: PropTypes.func,
    /**
     * List of initially checked sites
     */
    initialSelectedSiteIds: PropTypes.arrayOf(PropTypes.string),
    initialGroupBySiteGroup: PropTypes.bool,
    /**
     * Table prop overrides
     */
    tableProps: PropTypes.object,
    /**
     * List of sites to display from, this would be used in conjuction with fetchSites set to false
     */
    sites: PropTypes.arrayOf(
      PropTypes.shape({
        Id: PropTypes.string,
        SiteGroupId: PropTypes.number,
        SiteGroupName: PropTypes.string,
        IsLicensed: PropTypes.bool,
        IsActive: PropTypes.bool,
      })
    ),
    /**
     * Determines whether this component would retrieve the list of sites itself
     */
    fetchSites: PropTypes.bool,
    /**
     * Pass in your own API
     * The sites must be in response.data.Data
     */
    fetchSitesApi: PropTypes.any,
    fetchSitesApiParams: PropTypes.arrayOf(PropTypes.any),
    fetchAllSites: PropTypes.bool,

    showOptions: PropTypes.bool,
    isActive: PropTypes.bool,
    isLicensed: PropTypes.bool,
    getSitesCallback: PropTypes.func,
    /**
     * Group by toggle change
     */
    onGroupBySiteGroupChange: PropTypes.func,
    /**
     * Allows you to control the selected sites using the initialSelectedSiteIds prop
     */
    enableReinitialize: PropTypes.bool,
    /**
     * Defines additional columns to be displayed in the table
     * */
    additionalColumns: PropTypes.arrayOf(PropTypes.any),
  };
  static defaultProps = {
    /**
     * @param {[String]} selectedSiteIds - List of selected Sites
     */
    onChange: (selectedSiteIds) => {},
    sites: [],

    fetchSites: true,
    fetchSitesApi: null,
    fetchSitesApiParams: [],
    fetchAllSites: false,

    initialSelectedSiteIds: [],
    initialGroupBySiteGroup: false,
    tableProps: {},
    enableReinitialize: false,

    showOptions: true,
    // Filter out sites with isActive set to false
    isActive: true,
    // Filter out sites with isLicensed set to false
    isLicensed: true,
    getSitesCallback: () => {},
    onGroupBySiteGroupChange: () => {},

    showPageSizeOptions: false,
    defaultPageSize: 10,
    filterable: true,
    sortable: false,
  };

  constructor(props) {
    super(props);

    const { initialSelectedSiteIds } = props;
    const selectedSiteIds = initialSelectedSiteIds.reduce(
      (accumulator, current) => {
        accumulator[current] = true;
        return accumulator;
      },
      {}
    );

    const filteredSites = this.filterSites(props.sites);

    this.state = {
      // set loading to true, if this component is to request the sites itself
      loading: props.fetchSites,
      sites: filteredSites,
      groupBySiteGroup: props.initialGroupBySiteGroup,
      selectedSiteIds,
    };
  }

  /**
   * Filters sites based on the props
   * Only filter if the concerning prop is set to true, otherwise do not filter out
   */
  filterSites(sites) {
    const { isActive, isLicensed, isPrimary } = this.props;
    const matches = {
      ...(isActive && { IsActive: true }),
      ...(isLicensed && { IsLicensed: true }),
    };
    let filteredSites = _.filter(sites, _.matches(matches));
    return isPrimary
      ? filteredSites.filter((site) => site.PrimarySiteId === site.Id)
      : filteredSites;
  }

  async componentDidMount() {
    const {
      fetchSites,
      fetchSitesApi,
      fetchSitesApiParams,
      fetchAllSites,
    } = this.props;

    if (fetchSites) {
      try {
        const siteResponse = await (fetchSitesApi
          ? fetchSitesApi(...fetchSitesApiParams)
          : MPWWebApi.getGeneric(`Sitemaster?allSites=${fetchAllSites}`));

        const sites = _.get(siteResponse, 'data.Data', []);
        const filteredSites = this.filterSites(sites);
        this.setState({
          sites: filteredSites,
          loading: false,
        });
        this.props.getSitesCallback(filteredSites);
      } catch (error) {
        this.setState({
          sites: [],
          loading: false,
        });
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { initialSelectedSiteIds, enableReinitialize } = this.props;
    const prevselectedSiteIds = prevState.selectedSiteIds;
    const currentSelectedSiteIds = this.state.selectedSiteIds;

    const currentNumSelected = countSelected(currentSelectedSiteIds);
    const prevNumSelected = countSelected(prevselectedSiteIds);

    if (prevNumSelected !== currentNumSelected) {
      const selectedSiteIdsArray = Object.entries(currentSelectedSiteIds)
        .filter((v) => v[1])
        .map((v) => v[0]);
      if (this.props.onChange) this.props.onChange(selectedSiteIdsArray);
    }
    if (
      enableReinitialize &&
      initialSelectedSiteIds &&
      prevProps.initialSelectedSiteIds &&
      prevProps.initialSelectedSiteIds !== initialSelectedSiteIds &&
      initialSelectedSiteIds.length > 0
    ) {
      this.setNewSites(initialSelectedSiteIds, true);
    }
  }

  setNewSites(sites = [], checked = []) {
    const update = sites.reduce((accumulator, current) => {
      accumulator[current] = checked;
      return accumulator;
    }, {});
    this.setState({ selectedSiteIds: { ...update } });
  }

  selectSites(sites = [], checked = false) {
    const update = sites.reduce((accumulator, current) => {
      accumulator[current] = checked;
      return accumulator;
    }, {});
    this.setState({
      selectedSiteIds: {
        ...this.state.selectedSiteIds,
        ...update,
      },
    });
  }

  getTableProps() {
    const {
      tableProps,
      fetchSites,
      showPageSizeOptions,
      defaultPageSize,
      filterable,
      sortable,
      additionalColumns,
      selectDistrictMode,
      className,
    } = this.props;
    const { selectedSiteIds, sites, groupBySiteGroup } = this.state;
    const commonHeaderStyles = {
      display: 'flex',
      alignItems: 'center',
      padding: '7px 0',
    };
    const props = {
      className,
      data: fetchSites ? sites : this.props.sites,
      showPageSizeOptions,
      showPagination: true,
      collapseOnDataChange: false,
      collapseOnSortingChange: false,
      defaultPageSize,
      filterable,
      columns: [
        {
          Header: (rows) => {
            const siteIds = groupBySiteGroup
              ? _.flattenDeep(
                  rows.data.map((sg) => sg._subRows.map((s) => s.Id))
                )
              : rows.data.map((s) => s.Id);
            const checkedSites = siteIds
              .map((v) => Boolean(selectedSiteIds[v]))
              .filter((v) => v);

            const hasAllSitesChecked = checkedSites.length === siteIds.length;
            return (
              <div
                style={{
                  marginLeft: 5,
                  ...commonHeaderStyles,
                }}
              >
                <Checkbox
                  checked={hasAllSitesChecked}
                  onChange={(e, data) => {
                    this.selectSites(siteIds, data.checked);
                  }}
                />

                <span>
                  &nbsp;{selectDistrictMode ? 'District Id(s)' : 'Site Id(s)'}
                </span>
              </div>
            );
          },
          headerStyle: {
            textAlign: 'left',
          },
          id: 'Id',
          accessor: (d) => d.Id,
          width: 150,
          aggregate: (vals, rows) => {
            const checkedSites = vals
              .map((v) => Boolean(selectedSiteIds[v]))
              .filter((v) => v);
            const hasAllSitesInSiteGroupChecked =
              checkedSites.length === vals.length;
            return hasAllSitesInSiteGroupChecked;
          },
          Aggregated: (row) => {
            const sites = row.subRows;
            const siteIdsInSiteGroup = sites.map((s) => s.Id);
            return (
              <Checkbox
                label="All"
                checked={row.value}
                onChange={(e, data) => {
                  this.selectSites(siteIdsInSiteGroup, data.checked);
                }}
              />
            );
          },
          Cell: ({ row, original }) => {
            if (original) {
              return (
                <div style={{ paddingLeft: groupBySiteGroup ? 20 : 0 }}>
                  <Checkbox
                    label={original.Id}
                    checked={!!selectedSiteIds[original.Id]}
                    onChange={(e, data) => {
                      this.selectSites([original.Id], data.checked);
                    }}
                  />
                </div>
              );
            }
          },
          filterable: groupBySiteGroup ? false : true,
          sortable,
        },
        {
          Header: selectDistrictMode
            ? 'District Description(s)'
            : 'Site Description(s)',
          headerStyle: {
            textAlign: 'left',
            marginLeft: filterable ? 0 : 5,
            ...commonHeaderStyles,
          },
          id: 'Description',
          accessor: (d) => d.Description,
          sortable,
          aggregate: (vals, rows) => {
            return vals.join(', ');
          },
        },
        {
          Header: 'Site Group',
          resizable: false,
          headerStyle: {
            textAlign: 'left',
            marginLeft: filterable ? 0 : 5,
            ...commonHeaderStyles,
          },
          id: 'SiteGroupName',
          accessor: (d) => d.SiteGroupName,
          aggregate: (vals, rows) => {
            return vals[0];
          },
          Aggregated: (row) => {
            return <span>{row.value}</span>;
          },
          sortable,
        },
      ],

      pivotBy: [],
    };
    /**
     * pivotBy needs to be defined as empty array initially
     *
     */
    if (groupBySiteGroup) {
      props.pivotBy = ['SiteGroupName'];
    }

    if (selectDistrictMode) {
      props.columns.splice(2, 1);
    }

    if (Array.isArray(additionalColumns)) {
      props.columns.push(...additionalColumns);
    }

    return { ...props, ...tableProps };
  }

  onGroupBySiteGroupChange = (e, data) => {
    const nextValue = data.checked;
    this.setState({ groupBySiteGroup: nextValue });
    this.props.onGroupBySiteGroupChange(nextValue);
  };

  renderOptions() {
    const { groupBySiteGroup } = this.state;
    return (
      <Checkbox
        checked={groupBySiteGroup}
        toggle
        label="Select Sites by Site Group"
        onChange={this.onGroupBySiteGroupChange}
      />
    );
  }

  render() {
    const { loading } = this.state;
    const {
      showOptions,
      loading: propsLoading,
      selectDistrictMode,
    } = this.props;

    return (
      <React.Fragment>
        {showOptions && !selectDistrictMode && this.renderOptions()}
        <ReactTable
          loading={loading || propsLoading}
          {...this.getTableProps()}
          defaultFilterMethod={caseInsensitiveFilter}
        />
      </React.Fragment>
    );
  }
}

export default SelectSitesTable;



// WEBPACK FOOTER //
// ./src/common/SelectSitesTable.js